import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../redux/store";
import { loginWithGoogle, loginWithEmail, signupUser } from "../redux/thunks/authThunks";
import Navbar from "./navbar";

const Auth: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const mode = searchParams.get("mode") === "register" ? "register" : "login";
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: ""
    });

    useEffect(() => {
        setForm({
            name: "",
            email: "",
            password: "",
            confirmPassword: "",
            role: ""
        });
    }, [mode]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const { name, email, password, confirmPassword } = form;

        if (mode === "register") {
            if (!name.trim()) return alert("Name is required.");
            if (password !== confirmPassword) return alert("Passwords do not match.");
            if (!form.role) return alert("Please select a role.");
        }

        if (!email || !password) {
            return alert("Email and Password are required.");
        }

        if(mode === 'login'){
            
            const result = await dispatch(loginWithEmail({ email, password }));
            if (loginWithEmail.fulfilled.match(result)) {
                navigate("/dashboard");
            } else {
                alert(result.payload);
            }
        }else if(mode === 'register'){
            const result = await dispatch(signupUser({ name, email, password, confirmPassword, role: form.role, type: 'website' }));
            if (signupUser.fulfilled.match(result)) {
                navigate("/dashboard");
            } else {
                alert(result.payload);
            }
        }
    };

    const handleGoogleLogin = async (idToken: string) => {
        try {
            const result = await dispatch(loginWithGoogle({ idToken }));
            if (loginWithGoogle.fulfilled.match(result)) {
                navigate("/dashboard");
            } else {
                alert("Google login failed.");
            }
        } catch (error) {
            console.error("Google Login Error:", error);
        }
    };

    const switchMode = () => {
        const newMode = mode === "login" ? "register" : "login";
        setSearchParams({ mode: newMode });
    };

    return (
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID!}>
            <div className="min-h-screen  bg-red-500/5 flex flex-col">
                <Navbar navLight playBtn={false} bgLight={false} navCenter={true} />

                <div className="flex flex-1 flex-col md:flex-row" style={{marginTop: '61px'}}>
                    <div className="md:w-1/2 w-full flex items-center justify-center p-10">
                        <img
                            src={require("../assets/images/app-logo-dark.png")}
                            alt="Illustration"
                            className="max-w-md w-full"
                        />
                    </div>

                    <div className="md:w-1/2 w-full flex items-center justify-center p-10">
                        <div className="w-full max-w-md">
                            <div className="mb-8 text-center">
                                <h1 className="text-3xl font-bold text-white">
                                    {mode === "login" ? "Welcome Back" : "Create an Account"}
                                </h1>
                                <p className="mt-2 text-white">
                                    {mode === "login"
                                        ? "Login to access your dashboard."
                                        : "Register to get started."}
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4 bg-white p-8 rounded-lg shadow-md">
                                {mode === "register" && (
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Full Name"
                                        value={form.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border rounded-lg"
                                        required
                                    />
                                )}
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email"
                                    value={form.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border rounded-lg"
                                    required
                                />
                                <input
                                    type="password"
                                    name="password"
                                    placeholder="Password"
                                    value={form.password}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border rounded-lg"
                                    required
                                />
                                {mode === "register" && (
                                    <>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            placeholder="Confirm Password"
                                            value={form.confirmPassword}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                            required
                                        />
                                        {/* 👇 Role Select Input */}
                                        <select
                                            name="role"
                                            value={form.role}
                                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-lg"
                                            required
                                        >
                                            <option value="">Select Role</option>
                                            <option value="user">User</option>
                                            <option value="photographer">photographer</option>
                                        </select>
                                    </>
                                )}
                                <button
                                    type="submit"
                                    className="w-full bg-black text-white py-2 rounded-lg hover:bg-grey transition"
                                >
                                    {mode === "login" ? "Login" : "Register"}
                                </button>
                            </form>

                            <div className="my-4 text-black text-center">OR</div>

                            <div className="mb-8 text-center flex justify-center">
                                <GoogleLogin
                                    onSuccess={(res) => {
                                        const idToken = res.credential;
                                        if (idToken) handleGoogleLogin(idToken);
                                    }}
                                    onError={() => {
                                        console.log("Google login failed");
                                    }}
                                />
                            </div>

                            <div className="mt-4 text-sm text-center text-white">
                                {mode === "login" ? (
                                    <>
                                        Don’t have an account?{" "}
                                        <button
                                            onClick={switchMode}
                                            className="text-indigo-600 font-semibold text-white"
                                        >
                                            Sign up
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        Already have an account?{" "}
                                        <button
                                            onClick={switchMode}
                                            className="text-indigo-600 font-semibold text-white"
                                        >
                                            Login
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </GoogleOAuthProvider>
    );
};

export default Auth;
